// stores/mantap.js
import { v4 as uuidv4 } from 'uuid';

// stores/mantap.js
import LuckyDrawAPI from '../../api/mantap';

export const state = {
  // Participants data
  participants: [],
  currentParticipant: null,
  winners: [],
  
  // Pagination
  pagination: {
    current_page: 1,
    total_pages: 1,
    total_count: 0
  },
  
  // Loading states
  loading: {
    participants: false,
    registration: false,
    winnerSelection: false,
    clearingWinners: false
  },
  isParticipantsLoading: false,
  
  // Error states
  errors: {
    participants: null,
    registration: null,
    winnerSelection: null,
    clearingWinners: null
  },
  
  // Filters and search
  filters: {
    searchQuery: '',
    filterType: 'all', // 'all', 'winners', 'participants'
    registeredFrom: null,
    registeredTo: null
  }
};

export const getters = {
  // Participants
  allParticipants: (state) => state.participants,
  currentParticipant: (state) => state.currentParticipant,
  
  // Winners
  allWinners: (state) => state.winners,
  winnersCount: (state) => state.winners.length,
  
  // Filtered participants (client-side filtering for UI)
  filteredParticipants: (state) => {
    let filtered = state.participants;
    
    // Filter by winner status
    if (state.filters.filterType === 'winners') {
      filtered = filtered.filter(p => p.additional_attributes?.won_prize === true);
    } else if (state.filters.filterType === 'participants') {
      filtered = filtered.filter(p => !p.additional_attributes?.won_prize);
    }
    
    // Search filter
    if (state.filters.searchQuery) {
      const query = state.filters.searchQuery.toLowerCase();
      filtered = filtered.filter(p => 
        p.name?.toLowerCase().includes(query) ||
        p.additional_attributes?.unit_number?.toLowerCase().includes(query) ||
        p.additional_attributes?.condominium?.toLowerCase().includes(query)
      );
    }
    
    return filtered;
  },
  
  // Pagination info
  paginationInfo: (state) => state.pagination,
  
  // Loading states
  isLoadingParticipants: (state) => state.isParticipantsLoading,
  isLoadingRegistration: (state) => state.loading.registration,
  isLoadingWinnerSelection: (state) => state.loading.winnerSelection,
  isLoadingClearingWinners: (state) => state.loading.clearingWinners,
  
  // Error states
  participantError: (state) => state.errors.participants,
  registrationError: (state) => state.errors.registration,
  winnerSelectionError: (state) => state.errors.winnerSelection,
  clearingWinnersError: (state) => state.errors.clearingWinners,
  
  // Stats
  totalParticipants: (state) => state.pagination.total_count,
  nonWinnerCount: (state) => state.participants.filter(p => !p.additional_attributes?.won_prize).length
};

export const mutations = {
  // Set participants
  SET_PARTICIPANTS(state, participants) {
    state.participants = participants || []; 
  },

  // Set loading state for participants
  SET_PARTICIPANTS_LOADING(state, isLoading) {
    state.isParticipantsLoading = isLoading;
  },
  
  // Set current participant
  SET_CURRENT_PARTICIPANT(state, participant) {
    state.currentParticipant = participant;
  },
  
  // Set winners
  SET_WINNERS(state, winners) {
    state.winners = winners;
  },
  
  // Add or update participant
  UPSERT_PARTICIPANT(state, participant) {
    const index = state.participants.findIndex(p => p.id === participant.id);
    if (index !== -1) {
      state.participants.splice(index, 1, participant);
    } else {
      state.participants.unshift(participant);
    }
  },
  
  // Update participant as winner
  MARK_AS_WINNER(state, { contactId, wonAt }) {
    const participant = state.participants.find(p => p.id === contactId);
    if (participant) {
      if (!participant.additional_attributes) {
        participant.additional_attributes = {};
      }
      participant.additional_attributes.won_prize = true;
      participant.additional_attributes.won_at = wonAt;
      
      // Also add to winners list if not already there
      if (!state.winners.find(w => w.id === contactId)) {
        state.winners.push(participant);
      }
    }
  },
  
  // Clear all winners
  CLEAR_ALL_WINNERS(state) {
    state.participants.forEach(participant => {
      if (participant.additional_attributes) {
        participant.additional_attributes.won_prize = false;
        delete participant.additional_attributes.won_at;
      }
    });
    state.winners = [];
  },
  
  // Set pagination
  SET_PAGINATION(state, pagination) {
    state.pagination = {
      ...state.pagination,
      ...pagination
    };
  },
  
  SET_LOADING(state, payload) {
    if (!payload || !payload.key) return;
    const { key, value } = payload;
    if (state.loading && Object.prototype.hasOwnProperty.call(state.loading, key)) {
      state.loading[key] = value;
    }
  },

  SET_ERROR(state, payload) {
    if (!payload || !payload.key) return;
    const { key, error } = payload;
    if (state.errors && Object.prototype.hasOwnProperty.call(state.errors, key)) {
      state.errors[key] = error;
    }
  },

  CLEAR_ERROR(state, key) {
    if (!key) return;
    if (state.errors && Object.prototype.hasOwnProperty.call(state.errors, key)) {
      state.errors[key] = null;
    }
  },
  
  // Filter mutations
  SET_FILTERS(state, filters) {
    state.filters = { ...state.filters, ...filters };
  },
  
  RESET_FILTERS(state) {
    state.filters = {
      searchQuery: '',
      filterType: 'all',
      registeredFrom: null,
      registeredTo: null
    };
  }
};

export const actions = {
  /**
   * Register a contact for lucky draw
   */
  async registerForLuckyDraw({ commit, dispatch }, { contactId, registrationData }) {
    commit('SET_LOADING', { key: 'registration', value: true });
    commit('CLEAR_ERROR', 'registration');
    
    try {
      const response = await LuckyDrawAPI.updateLuckyDraw({
        id: contactId,
        ...registrationData
      });
      
      const participant = response.data.payload;
      commit('UPSERT_PARTICIPANT', participant);
      commit('SET_CURRENT_PARTICIPANT', participant);
      
      // Refresh participants list to get updated counts
      await dispatch('fetchParticipants', { 
        accountId, 
        page: 1, // Reset to first page to see new registration
        filter: 'all' 
      });
      
      return { success: true, participant };
      
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Registration failed';
      commit('SET_ERROR', { key: 'registration', error: errorMessage });
      return { success: false, error: errorMessage };
    } finally {
      commit('SET_LOADING', { key: 'registration', value: false });
    }
  },

  /**
   * Fetch lucky draw participants with filters
   */
  async fetchParticipants({ commit }, { 
    page = 1, 
    filter = 'all', 
    searchQuery = '',
    registeredFrom = null,
    registeredTo = null 
  }) {
    commit('SET_PARTICIPANTS_LOADING', true);
    commit('CLEAR_ERROR', 'participants');
    
    try {
      const response = await LuckyDrawAPI.getParticipants({
        page,
        filter,
        q: searchQuery,
        registered_from: registeredFrom,
        registered_to: registeredTo
      });
      
      const participants = response.data.payload;
      const meta = response.data.meta;
      
      commit('SET_PARTICIPANTS', participants);
      commit('SET_PAGINATION', {
        current_page: meta.current_page,
        total_pages: Math.ceil(meta.count / 15), // Assuming 15 per page
        total_count: meta.count
      });
      
      // Extract winners from participants
      const winners = participants.filter(p => p.additional_attributes?.won_prize);
      commit('SET_WINNERS', winners);
      
      // Update filters
      commit('SET_FILTERS', {
        searchQuery,
        filterType: filter,
        registeredFrom,
        registeredTo
      });
      
      return { success: true, participants, meta };
      
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to fetch participants';
      commit('SET_ERROR', { key: 'participants', error: errorMessage });
      return { success: false, error: errorMessage };
    } finally {
      commit('SET_PARTICIPANTS_LOADING', false);
    }
  },

  /**
   * Auto-select 3 winners
   */
  async autoSelectWinners({ commit, dispatch }) {
    commit('CLEAR_ERROR', 'winnerSelection');
    
    try {
      const response = await LuckyDrawAPI.autoSelectWinners();
      
      const winners = response.data.payload;
      commit('SET_WINNERS', winners);
      
      // Update participants with winner status
      winners.forEach(winner => {
        commit('UPSERT_PARTICIPANT', winner);
      });
      
      // Refresh participants list to reflect changes
      await dispatch('fetchParticipants', { 
        page: 1,
        filter: state.filters.filterType,
        searchQuery: state.filters.searchQuery,
        registeredFrom: state.filters.registeredFrom,
        registeredTo: state.filters.registeredTo
      });
      
      return { success: true, winners };
      
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to select winners';
      commit('SET_ERROR', { key: 'winnerSelection', error: errorMessage });
      return { success: false, error: errorMessage };
    } finally {
      // commit('SET_LOADING', { key: 'winnerSelection', value: false });
    }
  },

  /**
   * Manually mark single contact as winner
   */
  async markAsWinner({ commit, dispatch }, { contactId }) {
    try {
      const response = await LuckyDrawAPI.markAsWinner({ id: contactId });
      
      const winner = response.data.payload;
      const wonAt = winner.additional_attributes?.won_at || new Date().toISOString();
      
      commit('MARK_AS_WINNER', { contactId, wonAt });
      commit('UPSERT_PARTICIPANT', winner);
      
      // Refresh to get updated counts
      await dispatch('fetchParticipants', { 
        page: 1,
        filter: state.filters.filterType,
        searchQuery: state.filters.searchQuery,
        registeredFrom: state.filters.registeredFrom,
        registeredTo: state.filters.registeredTo
      });
      
      return { success: true, winner };
      
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to mark as winner';
      return { success: false, error: errorMessage };
    }
  },

  /**
   * Manually mark multiple contacts as winners (bulk)
   */
  async markAsWinnersBulk({ commit, dispatch }, { contactIds }) {
    try {
      const response = await LuckyDrawAPI.markAsWinners({ contact_ids: contactIds });
      
      const winners = response.data.payload;
      
      // Update each winner in state
      winners.forEach(winner => {
        const wonAt = winner.additional_attributes?.won_at || new Date().toISOString();
        commit('MARK_AS_WINNER', { contactId: winner.id, wonAt });
        commit('UPSERT_PARTICIPANT', winner);
      });
      
      // Refresh to get updated counts
      await dispatch('fetchParticipants', { 
        page: 1,
        filter: state.filters.filterType,
        searchQuery: state.filters.searchQuery,
        registeredFrom: state.filters.registeredFrom,
        registeredTo: state.filters.registeredTo
      });
      
      return { success: true, winners };
      
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to mark winners';
      return { success: false, error: errorMessage };
    }
  },

  /**
   * Clear all winners
   */
  async clearAllWinners({ commit, dispatch, state }) {
    commit('CLEAR_ERROR', 'clearingWinners');
    
    try {
      const response = await LuckyDrawAPI.clearWinners();
      
      commit('CLEAR_ALL_WINNERS');
      
      await dispatch('fetchParticipants', { 
        page: 1,
        filter: state.filters.filterType,
        searchQuery: state.filters.searchQuery,
        registeredFrom: state.filters.registeredFrom,
        registeredTo: state.filters.registeredTo
      });
      
      return { success: true, message: response.data.message, clearedCount: response.data.cleared_count };
      
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to clear winners';
      commit('SET_ERROR', { key: 'clearingWinners', error: errorMessage });
      return { success: false, error: errorMessage };
    } finally {
      commit('SET_LOADING', { key: 'clearingWinners', value: false });
    }
  },

  /**
   * Update filters and refetch data
   */
  async updateFilters({ commit, dispatch }, { accountId, filters }) {
    commit('SET_FILTERS', filters);
    await dispatch('fetchParticipants', { 
      accountId, 
      page: 1, // Reset to first page when filters change
      ...filters 
    });
  },

  /**
   * Reset all filters
   */
  async resetFilters({ commit, dispatch }, { accountId }) {
    commit('RESET_FILTERS');
    await dispatch('fetchParticipants', { accountId, page: 1 });
  },

  /**
   * Change page
   */
  async changePage({ commit, dispatch, state }, { page }) {
    await dispatch('fetchParticipants', {
      page,
      filter: state.filters.filterType,
      searchQuery: state.filters.searchQuery,
      registeredFrom: state.filters.registeredFrom,
      registeredTo: state.filters.registeredTo
    });
  }
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};